import { useLoaderData } from '@remix-run/react';
import { ActionFunctionArgs, json, LoaderFunctionArgs, MetaFunction, redirect } from '@vercel/remix';
import { UserSession } from '../../.server/user-session';
import { sanitizeRedirect } from '../../shared/utils/sanitize-redirect';
import { AuthLayout } from '../_auth/layout';
import { getSignInPageMetadata, SignInPageMetadata } from './meta.server';

export interface SignInLoaderData extends SignInPageMetadata {
  redirectUrl?: string;
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const unsafeRedirectUrl = url.searchParams.get('redirectUrl');
  const redirectUrl = sanitizeRedirect(unsafeRedirectUrl);
  const metadata = await getSignInPageMetadata(redirectUrl);

  return json<SignInLoaderData>({ ...metadata, redirectUrl });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.title ?? 'Sign In | Paragonix';
  const ogImage = data?.ogImage;

  const metaTags = [{ title: pageTitle }, { property: 'og:title', content: pageTitle }];

  if (ogImage) {
    metaTags.push({ property: 'og:image', content: ogImage });
  }

  return metaTags;
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const form = await request.formData();
  const token = form.get('token');
  const unsafeRedirectUrl = form.get('redirectUrl') as string | null;
  const redirectUrl = sanitizeRedirect(unsafeRedirectUrl);

  if (!token || typeof token !== 'string') {
    const signInRedirectParams = new URLSearchParams();
    if (redirectUrl && redirectUrl !== '/') {
      signInRedirectParams.set('redirectUrl', redirectUrl);
    }
    const queryString = signInRedirectParams.toString();

    return redirect(`/sign-in${queryString ? `?${queryString}` : ''}`);
  }

  return UserSession.create(token, request.headers.get('Cookie') ?? undefined, redirectUrl);
};

export default function Route() {
  const { onSubmit, error, mfaChallenge, submitMfaCode, cancelMfaChallenge, isSubmittingMfa, mfaError, isSendingMfaCode } =
    AuthLayout.useSignInFormSubmit();
  const loaderData = useLoaderData<typeof loader>();

  return (
    <>
      <AuthLayout.Header>
        <AuthLayout.Title>Sign in</AuthLayout.Title>

        <AuthLayout.Description>
          Dont have an account? <a href="/sign-up">Sign Up</a>
        </AuthLayout.Description>
      </AuthLayout.Header>

      <AuthLayout.FormContainer>
        <AuthLayout.SignInForm
          error={error}
          onSubmit={onSubmit}
          redirectUrl={loaderData?.redirectUrl}
          mfaChallenge={mfaChallenge}
          onMfaSubmit={submitMfaCode}
          onMfaCancel={cancelMfaChallenge}
          isSubmittingMfa={isSubmittingMfa}
          mfaError={mfaError}
          isSendingMfaCode={isSendingMfaCode}
        />
      </AuthLayout.FormContainer>
    </>
  );
}
